import { FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError, SearchResponse, search } from '../api';
import ParsedPanel from '../components/ParsedPanel';
import { examplesFor } from '../markets';
import ResultCard from '../components/ResultCard';
import CodeSamples, { searchSnippet } from '../components/CodeSamples';
import { Session } from '../session';

/**
 * Example queries come from the tenant's MARKET — see ../markets.ts.
 *
 * They have to, because a budget is only meaningful in a currency: "under 2000
 * a month" is a reasonable Harare query and returns NOTHING in South Africa,
 * where the seeded catalogue starts at R4 500. A demo whose own suggested query
 * returns zero results is worse than one with no suggestions.
 */

export default function Search({ session }: { session: Session }) {
  const examples = examplesFor(session.tenant.markets, session.tenant.currency);
  const [searchParams, setSearchParams] = useSearchParams();
  // A query from the URL (`?q=`, e.g. the marketing site's Run button) fills
  // the box and runs once, on arrival.
  const [arrivedWith] = useState(() => (searchParams.get('q') ?? '').trim().slice(0, 500));
  const [query, setQuery] = useState(arrivedWith);
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [lastQuery, setLastQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Held across renders so a fast second search cancels the first: without it
  // two in-flight requests can resolve out of order and show results for the
  // query the visitor already replaced.
  const inFlight = useRef<AbortController | null>(null);

  async function run(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;

    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;

    setBusy(true);
    setError(null);
    try {
      const result = await search(session.key, trimmed, controller.signal);
      if (controller.signal.aborted) return;
      setResponse(result);
      setLastQuery(trimmed);
      // The address bar follows the search, so a refresh or a shared link
      // shows the same results.
      setSearchParams({ q: trimmed }, { replace: true });
    } catch (err) {
      if (controller.signal.aborted || (err as Error)?.name === 'AbortError') return;
      setError(err instanceof ApiError ? err.friendly : 'Search failed.');
      setResponse(null);
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }

  // Once, even under StrictMode's double effects in development: run() would
  // cancel the first request anyway, but there is no reason to send it twice.
  const ranArrival = useRef(false);
  useEffect(() => {
    if (!arrivedWith || ranArrival.current) return;
    ranArrival.current = true;
    void run(arrivedWith);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submit(event: FormEvent) {
    event.preventDefault();
    void run(query);
  }

  return (
    <main className="page">
      <h1>Search the index</h1>
      <p className="lede">
        Ask the way a renter would. The service extracts the hard constraints it can and matches the
        rest semantically — there is no keyword list behind this.
      </p>

      <form className="card" onSubmit={submit}>
        <div className="searchbar">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={examples.searchPlaceholder}
            aria-label="Search query"
            autoFocus
          />
          <button type="submit" disabled={busy || !query.trim()}>
            {busy ? <><span className="spinner" /> Searching</> : 'Search'}
          </button>
        </div>

        <div className="chips" style={{ marginTop: 12 }}>
          {examples.queries.map((example) => (
            <button
              type="button"
              key={example}
              className="chip example"
              onClick={() => {
                setQuery(example);
                void run(example);
              }}
            >
              {example}
            </button>
          ))}
        </div>
      </form>

      {error && <div className="note error" style={{ marginTop: 16 }}>{error}</div>}

      {response && (
        <>
          <div className="grid two" style={{ marginTop: 16 }}>
            <ParsedPanel parsed={response.parsed} diagnostics={response.diagnostics} />

            <div className="card">
              <h3>Result</h3>
              <p style={{ margin: 0, fontSize: 22, fontWeight: 650 }}>
                {response.total.toLocaleString()}
                <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-dim)' }}>
                  {' '}matching {response.total === 1 ? 'listing' : 'listings'}
                </span>
              </p>
              <p className="footnote" style={{ marginTop: 4 }}>
                Showing the top {response.results.length}. The API returns document ids and
                evidence; a real integration hydrates the rest from its own database, so a stale
                index can never put a wrong price on a renter's screen.
              </p>

              {response.warnings?.map((warning) => (
                <div className="note warn" key={warning} style={{ marginTop: 10, marginBottom: 0 }}>
                  {warning}
                </div>
              ))}
            </div>
          </div>

          {response.suggestions && response.suggestions.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <h3>The service suggests</h3>
              <div className="chips">
                {response.suggestions.map((suggestion) => (
                  <span className="chip" key={suggestion.label}>
                    {suggestion.label}
                    {suggestion.extraResults ? ` (+${suggestion.extraResults})` : ''}
                  </span>
                ))}
              </div>
              <p className="footnote">
                Returned alongside the results so a portal can offer a way out of a query that is
                too narrow, instead of showing an empty page.
              </p>
            </div>
          )}

          <div className="results">
            {response.results.length === 0 ? (
              <div className="empty">
                Nothing matched that. Try relaxing a constraint — the demo index holds a few hundred
                listings, not a national portal's stock.
              </div>
            ) : (
              response.results.map((item) => <ResultCard key={item.doc_id} item={item} />)
            )}
          </div>

          <div className="card" style={{ marginTop: 20 }}>
            <h2>The call behind this page</h2>
            <p className="footnote" style={{ marginTop: 0, marginBottom: 12 }}>
              Exactly what the browser just sent, with your key. Nothing is translated by a demo
              backend — there isn't one.
            </p>
            <CodeSamples snippets={searchSnippet(lastQuery)} />
          </div>
        </>
      )}

      {!response && !error && (
        <div className="empty">Pick an example above, or type your own.</div>
      )}
    </main>
  );
}
